import { UserPermissions } from 'configs/routes';
import { AxiosContextProvider } from 'context/AxiosContextProvider';
import { UserContextProvider, useUserContext } from 'context/UserContextProvider';
import { isTestingEnvironment } from 'helpers/environments';
import DevTools from 'layout/DevTools';
import NavigateTo500 from 'layout/errors/NavigateTo500';
import UtilizationPage from 'pages/controlling/UtilizationPage';
import { Navigate, Route, Routes } from 'react-router-dom';

import NavigateTo404 from '../components/NavigateTo404';
import PusherFunction from '../components/Pusher';
import config from '../config';
import { LogoutPage } from '../layout/LogoutPage';
import { MainLayout } from '../layout/MainLayout';
import AccountsOverviewPage from './accounts/AccountsOverviewPage';
import AnnouncementFormPage from './announcement/AnnouncementFormPage';
import AnnouncementOverviewPage from './announcement/AnnouncementOverviewPage';
import AutomationStatusOverviewPage from './automations/AutomationStatusOverviewPage';
import BreakPage from './BreakPage';
import ChainDetailsPage from './chains/ChainDetailsPage';
import ChainFormPage from './chains/ChainFormPage';
import ChainManagerFormPage from './chains/ChainManagerFormPage';
import ChainsOverviewPage from './chains/ChainsOverviewPage';
import FormsOverviewPage from './configureForms/FormsOverviewPage';
import FormsVersionDetailsPage from './configureForms/FormsVersionDetailsPage';
import ConfigureDsbFormVersionDetailsPage from './configureForms/FormVersionFieldMappingPage';
import ConfigureMakesOverviewPage from './configureMakes/ConfigureMakesOverviewPage';
import MakesOverviewPage from './configureMakes/MakesOverviewPage';
import ControllingChartOverviewPage from './controlling/ControllingChartOverviewPage';
import ControllingOverviewPage from './controlling/ControllingOverviewPage';
import CreateLegalDocumentPage from './legalDocuments/CreateLegalDocumentPage';
import LegalDocumentDetailPage from './legalDocuments/LegalDocumentDetailPage';
import LegalDocumentsPage from './legalDocuments/LegalDocumentsPage';
import UpdateLegalDocumentPage from './legalDocuments/UpdateLegalDocumentPage';
import MaintenancePage from './MaintenancePage';
import PersonalSettingsFormPage from './personalSettings/PersonalSettingsFormPage';
import Dashboard from './promotion/dashboard/Dashboard';
import PromotionFormPage from './promotion/PromotionFormPage';
import PromotionOverviewPage from './promotion/PromotionOverviewPage';
import ServiceHistoryDetailsPage from './serviceHistory/ServiceHistoryDetailsPage';
import ServiceHistoryOverviewPage from './serviceHistory/ServiceHistoryOverviewPage';
import B2CServiceHistoryDetailsPage from './serviceHistoryB2C/B2CServiceHistoryDetailsPage';
import B2CServiceHistoryOverviewPage from './serviceHistoryB2C/B2CServiceHistoryOverviewPage';
import ServiceRecordDetailsPage from './serviceRecord/ServiceRecordDetailsPage';
import ServiceRecordOverviewPage from './serviceRecord/ServiceRecordOverviewPage';
import VinUpdatePage from './serviceRecord/VinUpdatePage';
import TasksOverviewPage from './tasks/TasksOverviewPage';
import WorkshopUpdateRequestDetailPage from './updateRequests/WorkshopUpdateRequestDetailsPage';
import WorkshopUpdateRequestOverviewPage from './updateRequests/WorkshopUpdateRequestOverviewPage';
import UserDetailsPage from './user/dangerZone/UserDetailsPage';
import UserFormPage from './user/UserFormPage';
import UserOverviewPage from './user/UserOverviewPage';
import WorkshopVerificationDetailPage from './verifications/WorkshopVerificationDetailsPage';
import WorkshopVerificationOverviewPage from './verifications/WorkshopVerificationOverviewPage';
import WorkshopVerificationPage from './verifications/WorkshopVerificationPage';
import B2CVerificationDetailPage from './verificationsB2C/B2CVerificationDetailsPage';
import B2CVerificationOverviewPage from './verificationsB2C/B2CVerificationOverviewPage';
import WhiteLabelFormPage from './whiteLabelOrganizations/WhiteLabelFormPage';
import WhiteLabelOrganizationDetailsPage from './whiteLabelOrganizations/WhiteLabelOrganizationDetailsPage';
import WhiteLabelOverviewPage from './whiteLabelOrganizations/WhiteLabelOverviewPage';
import AccountDetailsOverviewPage from './workshop/AccountDetailsOverviewPage';
import WorkshopDetailsPage from './workshop/WorkshopDetailsPage';
import WorkshopOverviewPage from './workshop/WorkshopOverviewPage';
import WorkshopUpdateDataPage from './workshop/WorkshopUpdateDataPage';

function AuthenticatedRouter() {
  const { user } = useUserContext();

  return (
    <Routes>
      <Route path="/" element={<Navigate to="/app/op" />} />
      <Route index element={<Navigate to="/app/op" />} />
      <Route path="/logout" element={<LogoutPage />} />
      <Route path="/errorPage" element={<NavigateTo500 />} />
      <Route
        path="op"
        element={
          <MainLayout
            permissions={isTestingEnvironment() ? Object.values(UserPermissions) : user?.permissions}
            pusher={config.pusher.enabled ? <PusherFunction user={user} /> : null}
          />
        }
      >
        <Route path="cashflow" element={<ControllingOverviewPage />} />
        <Route path="statistics" element={<ControllingChartOverviewPage />} />
        <Route path="utilization" element={<UtilizationPage />} />

        <Route index element={<Navigate to="dashboard" />} />
        <Route path="dashboard" element={<Dashboard />} />
        <Route path="invite-chain-manager/:id" element={<ChainManagerFormPage />} />
        <Route path="chains/:id" element={<ChainDetailsPage />} />
        <Route path="chains/:id/update" element={<ChainFormPage />} />
        <Route path="chains/create" element={<ChainFormPage />} />
        <Route path="chains" element={<ChainsOverviewPage />} />
        <Route path="workshop-verification-request" element={<WorkshopVerificationOverviewPage />} />
        <Route path="workshops/:id/workshop-verification-request/create" element={<WorkshopVerificationPage />} />
        <Route path="workshop-verification-request/:id" element={<WorkshopVerificationDetailPage />} />
        <Route path="b2c-verification-request" element={<B2CVerificationOverviewPage />} />
        <Route path="b2c-verification-request/:id" element={<B2CVerificationDetailPage />} />
        <Route path="b2c-verification-request/:id/:type" element={<B2CVerificationDetailPage />} />
        <Route path="b2c-service-history-request" element={<B2CServiceHistoryOverviewPage />} />
        <Route path="b2c-service-history-request/:id" element={<B2CServiceHistoryDetailsPage />} />
        <Route path="b2c-service-history-request/:id/:type" element={<B2CServiceHistoryDetailsPage />} />
        <Route path="workshop-verification-request/:id/:type" element={<WorkshopVerificationDetailPage />} />
        <Route path="workshops" element={<WorkshopOverviewPage />} />
        <Route path="workshops/:id" element={<WorkshopDetailsPage />} />
        <Route path="workshops/:id/update" element={<WorkshopUpdateDataPage />} />

        <Route path="workshop-update-request" element={<WorkshopUpdateRequestOverviewPage />} />
        <Route path="workshop-update-request/:id" element={<WorkshopUpdateRequestDetailPage />} />
        <Route path="workshop-update-request/:id/:type" element={<WorkshopUpdateRequestDetailPage />} />
        <Route path="whitelabel-organizations" element={<WhiteLabelOverviewPage />} />
        <Route path="whitelabel-organizations/:id" element={<WhiteLabelOrganizationDetailsPage />} />
        <Route path="whitelabel-organizations/create" element={<WhiteLabelFormPage />} />
        <Route path="whitelabel-organizations/update/:id" element={<WhiteLabelFormPage />} />

        <Route path="service-record" element={<ServiceRecordOverviewPage />} />
        <Route path="tasks" element={<TasksOverviewPage />} />
        <Route path="service-record/:id" element={<ServiceRecordDetailsPage />} />
        <Route path="service-record/:id/:type" element={<ServiceRecordDetailsPage />} />
        <Route path="service-record/:id/vin-update" element={<VinUpdatePage />} />
        <Route path="account/:account_id" element={<AccountDetailsOverviewPage />} />
        <Route path="account/:account_id/:type" element={<AccountDetailsOverviewPage />} />
        <Route path="accounts" element={<AccountsOverviewPage />} />

        <Route path="maintenance" element={<MaintenancePage />} />

        <Route path="service-history-request" element={<ServiceHistoryOverviewPage />} />
        <Route path="service-history-request/:id" element={<ServiceHistoryDetailsPage />} />
        <Route path="service-history-request/:id/:type" element={<ServiceHistoryDetailsPage />} />
        <Route path="legal-documents" element={<LegalDocumentsPage />} />
        <Route path="legal-document-version/create" element={<CreateLegalDocumentPage />} />
        <Route path="legal-document-version/:id" element={<LegalDocumentDetailPage />} />
        <Route path="legal-document-version/:id/update" element={<UpdateLegalDocumentPage />} />
        <Route path="dsb-form-version/:id" element={<ConfigureDsbFormVersionDetailsPage />} />

        <Route path="dsb-form/:id" element={<FormsVersionDetailsPage />} />
        <Route path="dsb-form" element={<FormsOverviewPage />} />
        <Route path="config/makes" element={<MakesOverviewPage />} />
        <Route path="config/makes-mapping" element={<ConfigureMakesOverviewPage />} />
        <Route path="promotions" element={<PromotionOverviewPage />} />
        <Route path="promotions/create" element={<PromotionFormPage />} />
        <Route path="promotions/:id/update" element={<PromotionFormPage />} />

        <Route path="announcements" element={<AnnouncementOverviewPage />} />
        <Route path="announcements/create" element={<AnnouncementFormPage />} />
        <Route path="announcements/:id/update" element={<AnnouncementFormPage />} />

        <Route path="users" element={<UserOverviewPage />} />
        <Route path="user/:id" element={<UserDetailsPage />} />
        <Route path="user/update/:id" element={<UserFormPage />} />
        <Route path="user/create" element={<UserFormPage />} />
        <Route path="personal-settings" element={<PersonalSettingsFormPage />} />

        <Route path="automations" element={<AutomationStatusOverviewPage />} />

        <Route path="break" element={<BreakPage />} />
      </Route>

      <Route path="*" element={<NavigateTo404 />} />
    </Routes>
  );
}

export default function AppLandingPage() {
  return (
    <AxiosContextProvider>
      <UserContextProvider>
        <AuthenticatedRouter />
        <DevTools />
      </UserContextProvider>
    </AxiosContextProvider>
  );
}
